import type { AccessSourceType, PermissionUpdateEntry } from '@thinkalpha/common/contracts/permissions.js';
import type { CreateRouteRequest, UpdateRouteRequest } from '@thinkalpha/locates-ws-client';
import type { CreateRouteResponse, Route, RoutesResponse, UpdateRouteResponse } from 'src/admin/LAB/contracts/routes';
import { lapi as axios } from 'src/api';

export const getFirmRoutes = (firmId: string | null): Promise<Route[]> | [] => {
    if (!firmId) return [];
    return axios.get<RoutesResponse>(`/v1/routes/firms/${firmId}`).then((x) => x.data.routes);
};

export const getAllRoutes = (firmId?: string): Promise<Route[]> => {
    // `firmId` scopes the list to routes the given firm authored (PLAT-13183).
    return axios
        .get<RoutesResponse>(`/v1/routes`, firmId ? { params: { firmId } } : undefined)
        .then((x) => x.data.routes);
};

export const getAccountRoutes = (accountId: string | null): Promise<Route[]> | [] => {
    if (!accountId) return [];
    return axios.get<RoutesResponse>(`/v1/routes/accounts/${accountId}`).then((x) => x.data.routes);
};

/**
 * Replace every entry `entityType`/`entityId` holds on the route with `entries`. The backend
 * clears the entity's existing entries first, so callers pass the full remaining list.
 */
export const setRoutePermission = ({
    entityId,
    entityType,
    routeId,
    entries,
}: {
    entityId: string;
    entityType: AccessSourceType;
    routeId: string;
    entries: PermissionUpdateEntry[];
}): Promise<Route> => {
    return axios
        .put<Route>(`/v1/routes/${routeId}/permissions/${entityType}/${entityId}`, entries)
        .then((x) => x.data)
        .catch((e) => {
            throw new Error(e);
        });
};

export const createRoute = (newRoute: CreateRouteRequest): Promise<Route> => {
    return axios.post<CreateRouteResponse>(`/v1/routes`, newRoute).then((x) => x.data.route);
};

// `PATCH /v1/routes/:id` is a full update accepting the client `UpdateRouteRequest` (name,
// privateName, description, providers, and the full configuration). Callers send the whole route so
// unedited fields round-trip unchanged.
export const updateRoute = (route: Pick<Route, 'id'> & UpdateRouteRequest): Promise<Route> => {
    const { id, ...body } = route;
    return axios
        .patch<UpdateRouteResponse>(`/v1/routes/${id}`, body satisfies UpdateRouteRequest)
        .then((x) => x.data.route);
};
